/**
 * Full permission catalog delivered by the backend.
 *
 * SOURCE OF TRUTH: mirrors `GET /permissions/available` exactly as delivered
 * by the backend, including the grade-correction permission (69 permissions, verified). This list is
 * frozen: never invent a name that is not in it. When the backend adds a new
 * permission, update `AVAILABLE_PERMISSIONS` first, then wire it into
 * `RoutePermissions` below.
 *
 * The eleven `mcp_*` permissions gate the external AI/MCP connector only.
 * No backoffice screen may declare one of them.
 */
export const AVAILABLE_PERMISSIONS = [
  // students / admission / documents / bulk imports / waitlist
  'students:view',
  'students:manage',
  'admission:view_forms',
  'admission:manage_forms',
  'documents:manage',
  'documents:manage_types',
  'bulk_imports:view',
  'bulk_imports:manage',
  'admission:view_preregistration',
  'admission:manage_preregistration',
  'admission:export_csv',
  'waitlist:view',

  // academic
  'study_plans:view',
  'study_plans:manage',
  'subjects:manage',
  'periods:manage',
  'academic_calendar:manage',
  'enrollment_windows:view',
  'enrollment_windows:manage',
  'academic_enrollments:view',
  'academic_enrollments:manage',
  'enrollments:manage',
  'group_cohorts:manage',
  'groups:manage',
  'group_schedules:manage',
  'attendance:manage',
  'grade_sheets:manage',
  'academic.grade_corrections.manage',
  'evaluations:manage',
  'propedeutic:view',
  'propedeutic:manage',

  // finance
  'finance:view_payments',
  'finance:view_reports',
  'payments:view',
  'payments:manage',
  'payment.concepts.manage',
  'payment_plans:manage',
  'payment_references:manage',

  // staff, users, platform
  'professors:manage',
  'administratives:view',
  'administratives:manage',
  'users:view',
  'users:manage_status',
  'users:reset_password',
  'users:manage_mcp_access',
  'audit_log:view',
  'dashboard:view',
  'scholarships:view',
  'scholarships:manage',
  'diplomas:manage',
  'consulting:manage',
  'external_participants:view',
  'external_participants:manage',
  'extra_activities:manage',
  'extra_groups:manage',
  'extra_enrollments:view',
  'moodle_accounts:manage',
  'moodle_accounts:export',

  // MCP connector — external AI integration only, never a backoffice screen
  'mcp_students:view',
  'mcp_admission:view',
  'mcp_professors:view',
  'mcp_academic_catalog:view',
  'mcp_groups:view',
  'mcp_enrollments:view',
  'mcp_grades:view',
  'mcp_payments:view',
  'mcp_reports:view',
  'mcp_scholarships:view',
  'mcp_other:view',
] as const;

export type Permission = (typeof AVAILABLE_PERMISSIONS)[number];

/**
 * Permission catalog for `roleGuard`-protected routes and sidebar menu
 * items, reconciled against `AVAILABLE_PERMISSIONS` above.
 *
 * Every value here is one of the 69 real permissions — `satisfies
 * Record<string, Permission>` makes an invented or misspelled name a
 * compile error, and `permission-catalog.spec.ts` pins the same guarantee
 * at runtime so nobody can bypass the type check with a loose cast.
 *
 * A permission match is always sufficient ON ITS OWN in both consumers
 * (`roleGuard` and the sidebar's `canSeeItem`), with the existing
 * `roles: [...]` name check kept as a fallback for unmigrated routes.
 *
 * Not every screen has an entry here: where no catalog permission clearly
 * matched a screen's actual behavior, the route/menu item was left with its
 * `roles` fallback only instead of forcing a plausible-but-wrong name — see
 * `odd/tasks/001-permissions-migration.md` for the reconciliation notes.
 *
 * Two names this file previously carried — an invented finance
 * receipts-download permission and an invented certificates-management
 * permission — never existed in the real catalog and have been retired in
 * favor of `financeViewPayments`/`paymentsView` and `diplomasManage`
 * respectively. `permission-catalog.spec.ts` asserts the retired names no
 * longer appear anywhere in `src/app`.
 *
 * WHEN THE BACKEND ADDS A PERMISSION: add it to `AVAILABLE_PERMISSIONS`
 * first, then reference it here. Every route and sidebar item reads
 * permission names exclusively through this object, so reconciling is a
 * one-file edit instead of hunting scattered string literals.
 */
export const RoutePermissions = {
  // dashboard
  dashboardView: 'dashboard:view',

  // academic
  academicEnrollmentsView: 'academic_enrollments:view',
  studyPlansManage: 'study_plans:manage',
  subjectsManage: 'subjects:manage',
  periodsManage: 'periods:manage',
  academicCalendarManage: 'academic_calendar:manage',
  groupCohortsManage: 'group_cohorts:manage',
  groupsManage: 'groups:manage',
  groupSchedulesManage: 'group_schedules:manage',
  enrollmentsManage: 'enrollments:manage',
  gradeSheetsManage: 'grade_sheets:manage',
  academicGradeCorrectionsManage: 'academic.grade_corrections.manage',
  evaluationsManage: 'evaluations:manage',

  // admission
  admissionManageForms: 'admission:manage_forms',
  admissionExportCsv: 'admission:export_csv',
  admissionManagePreregistration: 'admission:manage_preregistration',
  bulkImportsManage: 'bulk_imports:manage',

  // finance
  financeViewPayments: 'finance:view_payments',
  financeViewReports: 'finance:view_reports',
  paymentsView: 'payments:view',
  paymentsManage: 'payments:manage',
  paymentConceptsManage: 'payment.concepts.manage',
  paymentPlansManage: 'payment_plans:manage',
  paymentReferencesManage: 'payment_references:manage',

  // students / staff
  studentsView: 'students:view',
  professorsManage: 'professors:manage',

  // certificates ("constancias") — confirmed against the component: it
  // issues Taller/Curso/Diplomado/Intervención formativa certificates with
  // a folio + QR template, i.e. diploma issuance, not a different concept.
  diplomasManage: 'diplomas:manage',

  // consulting — the catalog exposes exactly one consulting permission, so
  // every consulting screen (catalog, sales, moodle management) shares it.
  consultingManage: 'consulting:manage',

  // continuing education ("Ed. Continua")
  extraActivitiesManage: 'extra_activities:manage',
  extraGroupsManage: 'extra_groups:manage',
  moodleAccountsManage: 'moodle_accounts:manage',
  moodleAccountsExport: 'moodle_accounts:export',
  externalParticipantsManage: 'external_participants:manage',

  // platform
  auditLogView: 'audit_log:view',
} as const satisfies Record<string, Permission>;
